import React from 'react';

import { Box, Link, Text } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { useAnalytics } from '@r-client/shared/data/analytics';
import { useAuth } from '@r-client/shared/data/auth';
import {
  E_US_ACCOUNT_ACCESS_STATE,
  useJurisdiction,
  useUsAccountAccessState,
} from '@r-client/shared/feature/global-auth';
import { Dropdown } from '@r-client/shared/ui/dropdown';
import { BasicMenu, BasicMenuItem } from '@r-client/shared/ui/menu';
import { useHasWallet } from '@r-client/wallet/feature/session';

import { EditableEntities } from '../../editable-entities/editable-entities';
import { userNavItems } from '../../navigation-data';
import { TEditableEntities } from '../../types';
import {
  getUserTeasers,
  teasersToEditableEntities,
} from '../../utilities/teasers-to-editable-entities';

// header height (64px) + bottom spacing (16px)
const DROPDOWN_VERTICAL_OFFSET = 64 + 16;
const DROPDOWN_WIDTH = '222px';

export interface IUserDropdownProps {
  isFounder?: boolean;
  userSlug: string;
  triggerElement: React.ReactElement;
  editableOfferings?: TEditableEntities;
}

export const UserDropdown: React.FC<
  React.PropsWithChildren<IUserDropdownProps>
> = observer(function UserDropdown({
  userSlug,
  editableOfferings,
  triggerElement,
  isFounder,
}) {
  const { viewer, signOut } = useAuth();
  const teasers = teasersToEditableEntities(getUserTeasers(viewer));
  const { hasWallet } = useHasWallet();
  const analytics = useAnalytics();
  const jurisdiction = useJurisdiction();

  const allItems = userNavItems({
    userSlug,
    isFounder,
    mobile: false,
    hasWallet,
    hasAllowedOktaDomain: viewer?.info?.hasAllowedOktaDomain,
  }).filter(Boolean);

  let logoutItem: (typeof allItems)[number] | undefined;
  const regularItems = allItems.filter((item) => {
    if (item?.href === '/logout') {
      logoutItem = item;
      return false;
    }
    return true;
  });

  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    return signOut({ redirectTo: '/logout' });
  };

  const hasAccountInEU = jurisdiction.accounts.hasEuAccount;
  const hasAccountInUK = jurisdiction.accounts.hasUkAccount;
  const hasAccountInUS = jurisdiction.accounts.hasUsAccount;

  const {
    state: accessState,
    isUsAuthenticated: showAuthenticatedMenu,
    hasCrossRegionAccountAccess,
  } = useUsAccountAccessState(!!viewer?.info);

  const trackItemClick = (item: NonNullable<(typeof allItems)[number]>) => {
    analytics.track({
      name: 'user_menu_clicked',
      params: {
        label: `user_menu_${item.title.toLowerCase().replace(' ', '_')}`,
        ...item.trackingParams,
      },
    });
  };

  return (
    <Dropdown
      arrow
      placement="bottom-end"
      trigger="hover"
      content={
        <Box
          overflow="auto"
          overflowX="hidden"
          minW={DROPDOWN_WIDTH}
          maxHeight={`calc(100vh - ${DROPDOWN_VERTICAL_OFFSET}px)`}
        >
          <BasicMenu>
            {accessState === E_US_ACCOUNT_ACCESS_STATE.CAN_ONBOARD && (
              <>
                <BasicMenuItem>
                  <Link
                    variant="noDecoration"
                    href="/user/onboarding/create-us-account"
                    p="3"
                    color="grey.900"
                    display="block"
                  >
                    <Box minW="100%">Open US account</Box>
                  </Link>
                </BasicMenuItem>
                <BasicMenuItem>
                  <Link
                    variant="noDecoration"
                    href="/wallet"
                    p="3"
                    color="grey.900"
                    display="block"
                  >
                    <Box minW="100%">Create a Republic wallet</Box>
                  </Link>
                </BasicMenuItem>
              </>
            )}
            {showAuthenticatedMenu && (
              <>
                <EditableEntities
                  offerings={editableOfferings}
                  teasers={teasers}
                />
                {regularItems.map((item) => {
                  if (!item) return null;

                  return (
                    <BasicMenuItem key={item.href}>
                      <Link
                        variant="noDecoration"
                        href={item.href}
                        p="3"
                        color="grey.900"
                        display="block"
                        onClick={() => trackItemClick(item)}
                      >
                        <Box minW="100%">{item.title}</Box>
                      </Link>
                    </BasicMenuItem>
                  );
                })}
              </>
            )}
            {hasCrossRegionAccountAccess && (
              <Box borderTop="1px solid" borderColor="grey.50" bg="white">
                <Text size="body-sm" color="grey.500" p="3">
                  {hasAccountInUS && (hasAccountInEU || hasAccountInUK)
                    ? 'You also have investment accounts in:'
                    : 'Investment account opened also in:'}
                </Text>
                {hasAccountInEU && (
                  <BasicMenuItem>
                    <Link
                      variant="noDecoration"
                      href="https://europe.republic.com"
                      color="grey.900"
                      p="3"
                    >
                      European Union
                    </Link>
                  </BasicMenuItem>
                )}
                {hasAccountInUK && (
                  <BasicMenuItem>
                    <Link
                      variant="noDecoration"
                      href="https://europe.republic.com"
                      color="grey.900"
                      p="3"
                    >
                      United Kingdom
                    </Link>
                  </BasicMenuItem>
                )}
              </Box>
            )}

            {logoutItem && (
              <Box
                borderTop="1px solid"
                borderColor="grey.50"
                borderBottomRadius="lg"
                bg="white"
                overflow="hidden"
              >
                <BasicMenuItem>
                  <Link
                    variant="noDecoration"
                    href={logoutItem.href}
                    p="3"
                    color="grey.900"
                    display="block"
                    onClick={(e) => {
                      trackItemClick(logoutItem!);
                      handleLogout(e);
                    }}
                  >
                    <Box minW="100%">{logoutItem.title}</Box>
                  </Link>
                </BasicMenuItem>
              </Box>
            )}
          </BasicMenu>
        </Box>
      }
    >
      <div>{triggerElement}</div>
    </Dropdown>
  );
});
